import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
  Alert,
  Fade,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  CircularProgress,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import {
  CloudUpload as UploadIcon,
  Biotech as PathologyIcon,
  PlayArrow as AnalyzeIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Contrast as ContrastIcon,
  Brightness7 as BrightnessIcon,
  GridOn as GridIcon,
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import axios from 'axios';

// ── Environment-driven config — never hardcode in source ───────────────
const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_KEY  = process.env.REACT_APP_API_KEY  || 'test-key-123';

// ── Encoder identity — Prov-GigaPath foundation model ───────────────────
const ENCODER_NAME = 'Prov-GigaPath';
const ENCODER_DETAIL = 'ViT-g/14, 1.3B params — pretrained on 1.3B tissue tiles (Microsoft/Providence)';

// ── Slide / staining type options ───────────────────────────────────────
const stainOptions = [
  { value: 'he',          label: 'H&E (Hematoxylin & Eosin)' },
  { value: 'ihc',         label: 'IHC (Immunohistochemistry)' },
  { value: 'ziehl-neelsen', label: 'Ziehl-Neelsen (AFB Stain)' },
  { value: 'other',       label: 'Other / Unspecified' },
];

// ── Magnification options — affects patch tiling at inference ──────────
const magnificationOptions = [
  { value: '5x',  label: '5x  (overview)' },
  { value: '10x', label: '10x (low-power)' },
  { value: '20x', label: '20x (standard, Prov-GigaPath default)' },
  { value: '40x', label: '40x (high-power)' },
];

const UploadArea = styled(Paper)(({ theme, isDragging }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  borderRadius: theme.spacing(2),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const ImagePreviewContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxHeight: 400,
  backgroundColor: theme.palette.grey[900],
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  marginTop: theme.spacing(2),
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pathology-tabpanel-${index}`}
      aria-labelledby={`pathology-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════════════════
const PathologyAnalysis = () => {
  const navigate = useNavigate();

  const [selectedTab,      setSelectedTab]      = useState(0);
  const [file,              setFile]             = useState(null);
  const [isDragging,        setIsDragging]       = useState(false);
  const [isLoading,         setIsLoading]        = useState(false);
  const [progress,          setProgress]         = useState(0);
  const [result,            setResult]           = useState(null);
  const [error,             setError]            = useState(null);
  const [logs,              setLogs]             = useState([]);
  const [imageUrl,          setImageUrl]         = useState(null);
  const [zoomLevel,         setZoomLevel]        = useState(1);
  const [brightness,        setBrightness]       = useState(100);
  const [contrast,          setContrast]         = useState(100);
  const [stainType,         setStainType]        = useState('he');
  const [magnification,     setMagnification]    = useState('20x');
  const [patientId,         setPatientId]        = useState('');
  const [showPatchGrid,     setShowPatchGrid]    = useState(false);
  const [analysisHistory,   setAnalysisHistory]  = useState([]);

  const fileInputRef = useRef(null);
  const imageRef = useRef(null);

  // ── Cleanup object URL on unmount / change ───────────────────────────
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  // ── Logging (matches Acoustic / Image / Clinical pattern) ────────────
  const addLog = useCallback((message) => {
    setLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message,
    }]);
  }, []);

  // ── File handling ──────────────────────────────────────────────────
  const handleFileUpload = useCallback((uploadedFile) => {
    if (!uploadedFile) return;

    const isImage = uploadedFile.type.startsWith('image/');
    const isSvs   = uploadedFile.name.toLowerCase().endsWith('.svs');
    const isNdpi  = uploadedFile.name.toLowerCase().endsWith('.ndpi');

    if (!isImage && !isSvs && !isNdpi) {
      setError('Please upload a valid pathology slide (JPG, PNG, TIFF, SVS, NDPI)');
      return;
    }

    // Whole-slide images can legitimately be much larger than a CXR
    if (uploadedFile.size > 5 * 1024  * 1024 * 1024) {
      setError('File size exceeds 5 GB limit');
      return;
    }

    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }

    setFile(uploadedFile);
    setError(null);
    setResult(null);

    // Only build a browser preview for standard image types —
    // SVS/NDPI whole-slide formats are not natively renderable by <img>
    if (isImage) {
      const url = URL.createObjectURL(uploadedFile);
      setImageUrl(url);
    } else {
      setImageUrl(null);
      addLog('ℹ️ Whole-slide format detected — preview unavailable, will be tiled server-side');
    }

    if (!patientId) {
      const nameWithoutExt = uploadedFile.name.replace(/\.[^/.]+$/, '');
      setPatientId(nameWithoutExt);
    }

    addLog(`📁 Uploaded: ${uploadedFile.name} (${(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)`);
  }, [imageUrl, patientId, addLog]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileUpload(droppedFile);
    }
  }, [handleFileUpload]);

  const handleFileInput = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFileUpload(selectedFile);
    }
  }, [handleFileUpload]);

  const removeFile = useCallback(() => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setFile(null);
    setImageUrl(null);
    setResult(null);
    setError(null);
    setZoomLevel(1);
    setBrightness(100);
    setContrast(100);
    addLog('🗑️ Slide removed');
  }, [imageUrl, addLog]);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.25, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  }, []);

  // ── Run analysis ──────────────────────────────────────────────────
  const runAnalysis = useCallback(async () => {
    if (!file) {
      setError('Please upload a pathology slide first');
      return;
    }

    setIsLoading(true);
    setProgress(0);
    setError(null);
    setResult(null);
    addLog(`🚀 Starting pathology analysis with ${ENCODER_NAME}...`);

    const formData = new FormData();
    formData.append('patient_id', patientId || 'unknown');
    formData.append('file', file);
    formData.append('stain_type', stainType);
    formData.append('magnification', magnification);

    try {
      setProgress(15);
      addLog('📤 Uploading slide...');

      setProgress(30);
      addLog('🧩 Tiling whole-slide image into patches...');

      setProgress(50);
      addLog(`🧠 Encoding patches with ${ENCODER_NAME}...`);

      setProgress(70);
      addLog('🔬 Aggregating patch embeddings (ABMIL)...');

      // ✅ FIX: Using POST method with proper headers
      const response = await axios.post(
        `${API_URL}/v1/predict/pathology`,
        formData,
        {
          headers: {
            'X-CTF-API-Key': API_KEY,
            'Content-Type': 'multipart/form-data',
          },
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total > 0) {
              const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setProgress(Math.min(15 + pct * 0.55, 95));
            }
          },
        }
      );

      console.log('📥 Pathology API Response:', response.data);

      setProgress(100);
      const data = response.data;
      setResult(data);

      setAnalysisHistory(prev => ([
        {
          id:        Date.now(),
          patientId,
          timestamp: new Date().toISOString(),
          result:    data,
        },
        ...prev,
      ].slice(0, 10)));

      addLog('✅ Analysis complete!');
      addLog(`🎯 Prediction: ${data.predicted_label || 'Unknown'} — TB prob: ${((data.tb_probability || 0) * 100).toFixed(1)}%`);
      if (data.patch_count) {
        addLog(`🧩 Patches analyzed: ${data.patch_count}`);
      }

    } catch (err) {
      console.error('Pathology analysis error:', err);
      
      // Better error handling
      let errorMessage = err.message || 'Analysis failed';
      
      if (err.response?.status === 413) {
        errorMessage = 'File too large. Maximum size is 3GB.';
        addLog('❌ 413 Error: File too large');
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
        addLog(`❌ Server error: ${errorMessage}`);
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. The server is taking too long.';
        addLog('⏰ Request timeout');
      } else {
        addLog(`❌ Error: ${errorMessage}`);
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  }, [file, patientId, stainType, magnification, addLog]);

  const resetAnalysis = useCallback(() => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setFile(null);
    setResult(null);
    setError(null);
    setProgress(0);
    setLogs([]);
    setImageUrl(null);
    setZoomLevel(1);
    setBrightness(100);
    setContrast(100);
    setStainType('he');
    setMagnification('20x');
    setShowPatchGrid(false);
    setPatientId('');
    addLog('🔄 Analysis reset');
  }, [imageUrl, addLog]);

  // ── Download JSON result (matches Acoustic / Clinical pattern) ──────
  const handleDownload = useCallback(() => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `pathology_result_${patientId || 'unknown'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [result, patientId]);

  // ── Upload area ────────────────────────────────────────────────────
  const renderUploadArea = useCallback(() => (
    <UploadArea
      isDragging={isDragging}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInput}
        accept="image/*,.svs,.ndpi"
        style={{ display: 'none' }}
      />
      {file ? (
        <Box>
          <CheckIcon color="success" sx={{ fontSize: 60 }} />
          <Typography variant="h6">{file.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {file.type || 'whole-slide image'}
          </Typography>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={(e) => {
              e.stopPropagation();
              removeFile();
            }}
            sx={{ mt: 1 }}
          >
            Remove
          </Button>
        </Box>
      ) : (
        <Box>
          <UploadIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
          <Typography variant="h6" color="text.secondary">
            Drag & drop a pathology slide here
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to browse (JPG, PNG, TIFF, SVS, NDPI) - Max 3GB
          </Typography>
        </Box>
      )}
    </UploadArea>
  ), [file, isDragging, handleDragOver, handleDragLeave, handleDrop, handleFileInput, removeFile]);

  // ── Slide preview, with optional patch-grid overlay ──────────────────
  const renderImagePreview = useCallback(() => (
    <ImagePreviewContainer>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {imageUrl && (
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Uploaded pathology slide"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              transform: `scale(${zoomLevel})`,
              filter: `brightness(${brightness}%) contrast(${contrast}%)`,
              transition: 'transform 0.3s ease, filter 0.3s ease',
              objectFit: 'contain',
            }}
          />
        )}
        {!imageUrl && file && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
            No browser preview available for this whole-slide format.
            <br />
            Patches will be generated and analyzed server-side.
          </Typography>
        )}
        {!imageUrl && !file && (
          <Typography variant="body2" color="text.secondary">
            No slide loaded
          </Typography>
        )}
        {imageUrl && showPatchGrid && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'linear-gradient(rgba(0,255,140,0.35) 1px, transparent 1px), ' +
                'linear-gradient(90deg, rgba(0,255,140,0.35) 1px, transparent 1px)',
              backgroundSize: '14.28% 14.28%', // illustrative 7x7 patch grid overlay
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    </ImagePreviewContainer>
  ), [imageUrl, file, zoomLevel, brightness, contrast, showPatchGrid]);

  // ── Image controls — zoom / brightness / contrast / patch grid ──────
  const renderImageControls = useCallback(() => (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        🎛️ Slide Controls
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Zoom In">
            <IconButton onClick={handleZoomIn} size="small">
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton onClick={handleZoomOut} size="small">
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
            {`${(zoomLevel * 100).toFixed(0)}%`}
          </Typography>
          <Tooltip title="Toggle patch-tiling overlay (illustrative)">
            <IconButton
              onClick={() => setShowPatchGrid(prev => !prev)}
              size="small"
              color={showPatchGrid ? 'primary' : 'default'}
              disabled={!imageUrl}
            >
              <GridIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1 }}>
          <BrightnessIcon fontSize="small" />
          <Slider
            size="small"
            value={brightness}
            onChange={(e, v) => setBrightness(v)}
            min={50}
            max={150}
            sx={{ width: 100 }}
          />
          <ContrastIcon fontSize="small" />
          <Slider
            size="small"
            value={contrast}
            onChange={(e, v) => setContrast(v)}
            min={50}
            max={150}
            sx={{ width: 100 }}
          />
        </Box>
      </Box>
    </Paper>
  ), [zoomLevel, brightness, contrast, showPatchGrid, imageUrl, handleZoomIn, handleZoomOut]);

  // ── Results panel (matches Acoustic / Image / Clinical pattern) ──────
  const renderResults = useCallback(() => {
    if (!result) return null;

    const tbProb     = (result.tb_probability || 0) * 100;
    const confidence  = (result.confidence    || 0) * 100;

    return (
      <Fade in={!!result} timeout={500}>
        <Box>
          <Card
            sx={{
              mb: 3,
              bgcolor: result.predicted_label === 'TB' ? 'error.light' : 'success.light',
            }}
          >
            <CardContent>
              <Typography variant="h5" gutterBottom>
                🎯 Prediction: {result.predicted_label || 'Unknown'}
              </Typography>
              <Typography variant="h6">
                TB Probability: {tbProb.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={tbProb}
                sx={{ height: 12, borderRadius: 6, mt: 1 }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Confidence: {confidence.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Model: {result.encoder_version || ENCODER_NAME}
                </Typography>
                {result.patch_count && (
                  <Typography variant="body2" color="text.secondary">
                    Patches analyzed: {result.patch_count}
                  </Typography>
                )}
                {result.aggregation_method && (
                  <Typography variant="body2" color="text.secondary">
                    Aggregation: {result.aggregation_method}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Available: {String(result.available ?? 'N/A')}
                </Typography>
                {result.checkpoint_hash && (
                  <Typography variant="body2" color="text.secondary">
                    Checkpoint: {result.checkpoint_hash.slice(0, 16)}...
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {result.clinically_valid === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ Encoder-only checkpoint loaded. This prediction is NOT clinically valid.
            </Alert>
          )}

          {Array.isArray(result.top_patches) && result.top_patches.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  🔬 Highest-Attention Patches
                </Typography>
                {result.top_patches.slice(0, 5).map((patch, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Patch #{patch.id ?? idx + 1} {patch.region ? `(${patch.region})` : ''}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${((patch.attention_weight || 0) * 100).toFixed(1)}%`}
                      color={idx === 0 ? 'error' : 'default'}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            {result.predicted_label === 'TB'
              ? `Model detected TB-consistent tissue patterns with ${tbProb.toFixed(1)}% probability.`
              : `Model detected normal/non-TB tissue with ${(100 - tbProb).toFixed(1)}% confidence.`}
          </Alert>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ mt: 2 }}
            fullWidth
          >
            Download Result JSON
          </Button>
        </Box>
      </Fade>
    );
  }, [result, handleDownload]);

  // ══════════════════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════════════════
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>

      {/* ── Header — matches Acoustic / Image / Clinical exactly ──────── */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🔬 Pathology Analysis Lab
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Upload histopathology slides for AI-powered diagnostic analysis
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Encoder: {ENCODER_NAME} — {ENCODER_DETAIL}
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/platform')}>
            Back to Platform
          </Button>
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>

      <Grid container spacing={3}>

        {/* ── Left panel: upload + controls ────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📤 Upload Slide
              </Typography>

              {renderUploadArea()}

              {error && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}

              {file && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 160 }}>
                    <InputLabel>Stain Type</InputLabel>
                    <Select
                      value={stainType}
                      onChange={(e) => setStainType(e.target.value)}
                      label="Stain Type"
                    >
                      {stainOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 160 }}>
                    <InputLabel>Magnification</InputLabel>
                    <Select
                      value={magnification}
                      onChange={(e) => setMagnification(e.target.value)}
                      label="Magnification"
                    >
                      {magnificationOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {file && renderImagePreview()}
              {file && renderImageControls()}

              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Patient ID"
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  sx={{ mb: 1 }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={runAnalysis}
                  disabled={!file || isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <AnalyzeIcon />}
                >
                  {isLoading ? 'Analyzing...' : 'Run Analysis'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetAnalysis}
                  disabled={isLoading}
                  startIcon={<RefreshIcon />}
                >
                  Reset
                </Button>
              </Box>

              {isLoading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    {Math.round(progress)}% Complete
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Right panel: Results + Logs tabs — matches other pages ─────── */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)}>
                  <Tab label="📊 Results" />
                  <Tab label="📝 Logs" />
                </Tabs>
              </Box>

              <TabPanel value={selectedTab} index={0}>
                {result ? renderResults() : (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 300,
                  }}>
                    <PathologyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography color="text.secondary">
                      Upload a slide and click{' '}
                      <strong>"Run Analysis"</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Results will appear here
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              <TabPanel value={selectedTab} index={1}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {logs.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ py: 4 }}
                    >
                      No logs yet. Run analysis to see the process.
                    </Typography>
                  ) : (
                    logs.map((log, idx) => (
                      <Box
                        key={idx}
                        sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          [{log.timestamp}]
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {log.message}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </TabPanel>
            </CardContent>
          </Card>

          {/* ── Analysis history — matches Clinical pattern ─────────────── */}
          {analysisHistory.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analysis History (Last 10)
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {analysisHistory.map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {entry.patientId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Prediction: {entry.result.predicted_label || 'Unknown'}{' '}
                        | Confidence:{' '}
                        {((Number(entry.result.confidence || 0)) * 100).toFixed(2)}%{' '}
                        | TB prob:{' '}
                        {((Number(entry.result.tb_probability || 0)) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default PathologyAnalysis;
